import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Plus, Edit, Trash2, LogOut, Home } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    genre: '',
    year: new Date().getFullYear().toString()
  });
  const navigate = useNavigate();
  const { toast } = useToast();

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return null;
    }
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      toast({
        title: 'Fel',
        description: 'Kunde inte ladda projekt.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchProjects();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast({
      title: 'Utloggad',
      description: 'Du har loggats ut.'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const authHeader = getAuthHeader();
    if (!authHeader) return;

    try {
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, formData, authHeader);
        toast({
          title: 'Uppdaterat!',
          description: 'Projektet har uppdaterats.'
        });
      } else {
        await axios.post(`${API}/projects`, formData, authHeader);
        toast({
          title: 'Skapat!',
          description: 'Nytt projekt har skapats.'
        });
      }
      setIsDialogOpen(false);
      setFormData({ title: '', description: '', genre: '', year: new Date().getFullYear().toString() });
      setEditingProject(null);
      fetchProjects();
    } catch (error) {
      toast({
        title: 'Fel',
        description: error.response?.data?.detail || 'Något gick fel.',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      genre: project.genre,
      year: project.year
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Är du säker på att du vill ta bort detta projekt?')) return;
    
    const authHeader = getAuthHeader();
    if (!authHeader) return;

    try {
      await axios.delete(`${API}/projects/${projectId}`, authHeader);
      toast({
        title: 'Borttaget!',
        description: 'Projektet har tagits bort.'
      });
      fetchProjects();
    } catch (error) {
      toast({
        title: 'Fel',
        description: 'Kunde inte ta bort projektet.',
        variant: 'destructive'
      });
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingProject(null);
    setFormData({ title: '', description: '', genre: '', year: new Date().getFullYear().toString() });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img
              src="https://customer-assets.emergentagent.com/job_da9e6102-b17b-4e3a-82a5-102f912074e7/artifacts/esnq4twv_S2R_logo_750x750.png"
              alt="S2R Logo"
              className="h-10 w-auto"
            />
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-gray-300"
            >
              <Home className="w-4 h-4 mr-2" />
              Hemsida
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-gray-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logga ut
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-black">Hantera Projekt</h2>
            <p className="text-gray-600 mt-1">Lägg till, redigera eller ta bort dina portföljprojekt</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => handleDialogClose()}
                className="bg-black text-white hover:bg-gray-800"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nytt Projekt
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>
                  {editingProject ? 'Redigera Projekt' : 'Skapa Nytt Projekt'}
                </DialogTitle>
                <DialogDescription>
                  Fyll i projektinformationen nedan.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Projekttitel</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="IF Fotbollsklubb"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Beskrivning</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="En energifylld hyllningslåt..."
                    required
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="genre">Genre</Label>
                    <Input
                      id="genre"
                      value={formData.genre}
                      onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                      placeholder="Rock"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="year">År</Label>
                    <Input
                      id="year"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      placeholder="2024"
                      required
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDialogClose}
                  >
                    Avbryt
                  </Button>
                  <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                    {editingProject ? 'Uppdatera' : 'Skapa'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Laddar projekt...</p>
          </div>
        ) : projects.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-600 mb-4">Inga projekt än. Skapa ditt första projekt!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card key={project.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle>{project.title}</CardTitle>
                  <CardDescription>{project.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-4">
                    <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-700">
                      {project.genre}
                    </span>
                    <span className="text-sm text-gray-500">{project.year}</span>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => handleEdit(project)}
                      size="sm"
                      variant="outline"
                      className="flex-1"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Redigera
                    </Button>
                    <Button
                      onClick={() => handleDelete(project.id)}
                      size="sm"
                      variant="outline"
                      className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Ta bort
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;